import React, { createContext, useContext, useState } from 'react';
import { getThemeIcons } from './icons/iconThemeConfig';

/**
 * Development Theme Configuration
 *
 * ⚠️ THIS IS NOT A/B TESTING ⚠️
 *
 * This module provides theme switching for development and design testing purposes.
 * It allows developers to quickly switch between alternative design themes
 * to test visual consistency and styling choices.
 *
 * For production A/B testing, see:
 * - apps/web/src/contexts/ABTestContext.jsx (production A/B testing)
 * - apps/api/src/services/abTest/* (backend A/B testing services)
 *
 * Usage:
 * - Press Ctrl+Shift+A to toggle theme panel
 * - Select Red Theme (Mountain icon) or Emerald Theme (Compass icon)
 * - Theme preference saved in localStorage
 *
 * Location: apps/web/src/config/DevThemeConfig.jsx
 * Created: 2025-10-31
 * Part of: MVP 1.10 - Code migration (original ABTestContext.jsx)
 */

/**
 * Available theme variants for development
 */
const THEME_VARIANTS = {
  RED: 'redTheme',      // Red/Rose theme with Mountain icon
  EMERALD: 'emeraldTheme', // Emerald/Green theme with Compass icon
};

/**
 * Theme Configuration
 * Maps theme variant names to their respective color schemes and styles
 */
const THEME_CONFIG = {
  redTheme: {
    name: 'Red Theme',
    description: 'Original styling with Red/Rose color palette',
    // Brand icon component (from centralized icon theme config)
    ...getThemeIcons('redTheme'),
    // Backward compatibility alias
    BrandIcon: getThemeIcons('redTheme').brandIcon,
    iconName: getThemeIcons('redTheme').brandIconName,
    // Tailwind color classes
    primary: {
      50: 'rose-50',
      100: 'rose-100',
      200: 'rose-200',
      300: 'rose-300',
      400: 'rose-400',
      500: 'rose-500',
      600: 'rose-600',
      700: 'rose-700',
      800: 'rose-800',
      900: 'rose-900',
    },
    // Commonly used classes
    buttonPrimary: 'bg-rose-600 hover:bg-rose-700 text-white',
    buttonSecondary: 'border-rose-600 text-rose-600 hover:bg-rose-50',
    textPrimary: 'text-rose-600',
    textHover: 'hover:text-rose-700',
    bgLight: 'bg-rose-50',
    borderPrimary: 'border-rose-600',
    iconColor: 'text-rose-600',
    linkColor: 'text-rose-600',
    badgeBg: 'bg-rose-100',
    badgeText: 'text-rose-700',
  },
  emeraldTheme: {
    name: 'Emerald Theme',
    description: 'New styling with Emerald/Green color palette',
    // Brand icon component (from centralized icon theme config)
    ...getThemeIcons('emeraldTheme'),
    // Backward compatibility alias
    BrandIcon: getThemeIcons('emeraldTheme').brandIcon,
    iconName: getThemeIcons('emeraldTheme').brandIconName,
    // Tailwind color classes
    primary: {
      50: 'emerald-50',
      100: 'emerald-100',
      200: 'emerald-200',
      300: 'emerald-300',
      400: 'emerald-400',
      500: 'emerald-500',
      600: 'emerald-600',
      700: 'emerald-700',
      800: 'emerald-800',
      900: 'emerald-900',
    },
    // Commonly used classes
    buttonPrimary: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    buttonSecondary: 'border-emerald-600 text-emerald-600 hover:bg-emerald-50',
    textPrimary: 'text-emerald-600',
    textHover: 'hover:text-emerald-700',
    bgLight: 'bg-emerald-50',
    borderPrimary: 'border-emerald-600',
    iconColor: 'text-emerald-600',
    linkColor: 'text-emerald-600',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
  },
};

// localStorage key for theme persistence
const STORAGE_KEY = 'dev_theme_variant';

const DevThemeContext = createContext();

/**
 * useDevTheme Hook
 *
 * Access current development theme configuration.
 *
 * Returns:
 * - currentTheme: string - Current theme variant ('redTheme' | 'emeraldTheme')
 * - theme: Object - Theme configuration object
 * - switchTheme: Function - Switch to different theme variant
 * - isRedTheme: boolean - Whether red theme is active
 * - isEmeraldTheme: boolean - Whether emerald theme is active
 * - THEME_VARIANTS: Object - Available theme variants
 */
export const useDevTheme = () => {
  const context = useContext(DevThemeContext);
  if (!context) {
    throw new Error('useDevTheme must be used within DevThemeProvider');
  }
  return context;
};

/**
 * DevThemeProvider Component
 *
 * Provides development theme configuration to all child components.
 * Theme selection is persisted in localStorage.
 *
 * ⚠️ This is for DEVELOPMENT ONLY - not production A/B testing
 */
export const DevThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to Emerald theme
  const [currentTheme, setCurrentTheme] = useState(() => {
    try {
      const savedTheme = localStorage.getItem(STORAGE_KEY);
      if (savedTheme && Object.values(THEME_VARIANTS).includes(savedTheme)) {
        return savedTheme;
      }
      // Default to Emerald theme
      localStorage.setItem(STORAGE_KEY, THEME_VARIANTS.EMERALD);
      return THEME_VARIANTS.EMERALD;
    } catch (error) {
      console.error('[DevTheme] Error initializing theme:', error);
      return THEME_VARIANTS.EMERALD; // Default to Emerald on error
    }
  });

  /**
   * Switch to a different theme variant
   * @param {string} newTheme - Theme variant to switch to
   */
  const switchTheme = (newTheme) => {
    if (Object.values(THEME_VARIANTS).includes(newTheme)) {
      setCurrentTheme(newTheme);
      try {
        localStorage.setItem(STORAGE_KEY, newTheme);
      } catch (error) {
        console.error('[DevTheme] Error saving theme:', error);
      }
    } else {
      console.warn('[DevTheme] Invalid theme variant:', newTheme);
    }
  };

  const theme = THEME_CONFIG[currentTheme] || THEME_CONFIG[THEME_VARIANTS.EMERALD];

  const value = {
    currentTheme,
    theme,
    switchTheme,
    isRedTheme: currentTheme === THEME_VARIANTS.RED,
    isEmeraldTheme: currentTheme === THEME_VARIANTS.EMERALD,
    THEME_VARIANTS,
  };

  return (
    <DevThemeContext.Provider value={value}>
      {children}
    </DevThemeContext.Provider>
  );
};
